import type Sector from "@/types/Sector";
import type Section from "@/types/Section";
import { SeatingType } from "@/types/SeatingType";

/** One expansion panel on the sector step, the buyer continues the purchase with its Section. */
export interface SectionPanel {
  /** Section id when the Sector is split into Sections, Sector id otherwise. Matches the map shape's id. */
  id: string;
  sector: Sector;
  section: Section;
  soldOut: boolean;
  /** Cheapest ticket on sale, null when nothing is. */
  minPrice: number | null;
}

/** A Sector on the sector step's list. */
export interface SectorGroup {
  sector: Sector;
  /** Shown as a heading with one panel per Section, instead of a single panel for the whole Sector. */
  split: boolean;
  soldOut: boolean;
  /** Cheapest ticket on sale across the Sections with seats left, null when nothing is. */
  minPrice: number | null;
  panels: SectionPanel[];
}

/** Only selected-seat Sectors with more than one Section let the buyer pick a Section. */
function isSplit(sector: Sector): boolean {
  return sector.seating_type === SeatingType.SELECTED && sector.sections.length > 1;
}

function isSectionSoldOut(section: Section): boolean {
  return section.seats?.available === 0;
}

/**
 * Sections on sale. A Section with no ticket types for this event (the API leaves out those not open yet, or hidden once
 * closed), or a selected-seat Section with no seats set up, has nothing to sell, so it isn't listed. General Sectors
 * never have seats.
 */
function listedSections(sector: Sector): Section[] {
  const sections = sector.sections.filter(section => section.tickets.length > 0);

  if (sector.seating_type !== SeatingType.SELECTED) return sections;

  return sections.filter(section => section.seats?.total !== 0);
}

function isSectorSoldOut(sector: Sector, sections: Section[]): boolean {
  if (sector.capacity <= sector.sold) return true;

  return sector.seating_type === SeatingType.SELECTED && sections.every(isSectionSoldOut);
}

/** Types with no current batch have no price to compare. */
function minPrice(sections: Section[]): number | null {
  const prices = sections
    .flatMap(section => section.tickets)
    .map(type => type.current_batch?.price)
    .filter((price): price is number => price !== undefined);

  return prices.length > 0 ? Math.min(...prices) : null;
}

function panel(id: string, sector: Sector, section: Section, soldOut: boolean): SectionPanel {
  const price = minPrice([ section ]);

  // Seats can only be held at a price, so a selected-seat Section with no batch on sale can't be bought yet
  const nothingOnSale = sector.seating_type === SeatingType.SELECTED && price === null;

  return { id, sector, section, soldOut: soldOut || nothingOnSale, minPrice: price };
}

export function sectorGroups(sectors: Sector[]): SectorGroup[] {
  return sectors
    .map(sector => ({ sector, sections: listedSections(sector) }))
    .filter(({ sections }) => sections.length > 0)
    .map(({ sector, sections }) => {
      // Split even when only one of its Sections is listed, so that Section's map shape still opens it
      const split = isSplit(sector);
      const soldOut = isSectorSoldOut(sector, sections);

      const panels = split
        ? sections.map(section => panel(section.id, sector, section, soldOut || isSectionSoldOut(section)))
        : [ panel(sector.id, sector, sections[0], soldOut) ];

      const buyable = panels.filter(p => !p.soldOut).map(p => p.section);

      return { sector, split, soldOut, minPrice: minPrice(buyable), panels };
    })
    // A sold-out Sector that isn't split has nothing to show, as before
    .filter(group => group.split || !group.soldOut)
    // Greys out once none of its panels can be bought, whatever the reason
    .map(group => ({ ...group, soldOut: group.panels.every(p => p.soldOut) }));
}

/**
 * Panel opened by a click on the map shape with this id, which is linked to either a Sector or a Section.
 * A shape linked to a split Sector opens its first Section with seats left. Null means there's nothing
 * to buy through the shape, so it is disabled.
 */
export function panelForShape(groups: SectorGroup[], shapeId: string): SectionPanel | null {
  for (const group of groups) {
    const panel = group.sector.id === shapeId
      ? group.panels.find(p => !p.soldOut)
      : group.panels.find(p => p.id === shapeId);

    if (panel !== undefined) return panel.soldOut ? null : panel;
  }

  return null;
}
